import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../../components/ui/card';
import { Button, Table, Badge } from 'react-bootstrap';
import { useStock } from '../../../../../apps/hooks/stock/useStock';
import type { OrderSummary } from '../../../../../infrastructure/OMSClient';
import { toast } from 'react-hot-toast';
import { InboundStatus } from '../../../../../models/InboundStatus';
import { ROUTES } from '../../../../../routes/routes';
import { OrderLabelPrintButton } from '../../../orders/components/OrderLabelPrintButton';

interface ReadyToOutboundItem {
  orderId: string;
  orderDate: string;
  customerName: string;
  lineItemsCount: number;
  readyItems: Array<{
    itemId: string;
    sku: string;
    name: string;
    quantity: number;
  }>;
  originalOrder: OrderSummary;
}

export default function ReadyToBeOutbondedPage() {
  const [readyOrders, setReadyOrders] = useState<ReadyToOutboundItem[]>([]);
  const [loading, setLoading] = useState(false);

  const stock = useStock();

  const fetchReadyOrders = async () => {
    setLoading(true);
    try {
      const result = await stock.getReadyToBeOutbonded();
      const orders = result.orders || [];

      // Transform orders to ReadyToOutboundItem format
      const readyOrdersData: ReadyToOutboundItem[] = [];

      orders.forEach((order: OrderSummary) => {
        const readyItems = order.lineItems.filter((item) => item.inboundStatus === InboundStatus.INBOUNDED);

        if (readyItems.length > 0) {
          readyOrdersData.push({
            orderId: order.orderId,
            orderDate: order.orderDate,
            customerName: order.customer.name || order.customer.email,
            lineItemsCount: order.lineItems.length,
            readyItems: readyItems.map((item) => ({
              itemId: item.itemId || '',
              sku: item.sku,
              name: item.name,
              quantity: item.quantity,
            })),
            originalOrder: order,
          });
        }
      });

      setReadyOrders(readyOrdersData);
    } catch (error) {
      console.error('Error fetching ready orders:', error);
      toast.error(`Failed to fetch orders: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleOrderClick = (orderId: string) => {
    window.open(ROUTES.oms.orderDetail(orderId), '_blank');
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('ko-KR');
    } catch {
      return dateString;
    }
  };

  useEffect(() => {
    // Auto-fetch on component mount
    fetchReadyOrders();
  }, []);

  return (
    <div className="container mx-auto py-4 space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">WMS - Ready to be Outbonded</h1>
      </div>

      {/* Refresh Section */}
      <Card>
        <CardHeader>
          <div className="d-flex justify-content-between align-items-center">
            <CardTitle>Ready to be Outbonded Orders</CardTitle>
            <Button variant="primary" onClick={fetchReadyOrders} disabled={loading}>
              {loading ? 'Loading...' : 'Refresh'}
            </Button>
          </div>
        </CardHeader>
      </Card>

      {/* Results Section */}
      <Card>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2">Searching for orders...</p>
            </div>
          ) : readyOrders.length > 0 ? (
            <div>
              <div className="table-responsive">
                <Table striped bordered hover>
                  <thead className="table-dark">
                    <tr>
                      <th>Order ID</th>
                      <th>Order Date</th>
                      <th>Customer</th>
                      <th>Total Items</th>
                      <th>Ready Items</th>
                      <th>Ready Item Details</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {readyOrders.map((order, index) => (
                      <tr key={index}>
                        <td className="font-monospace">
                          <span
                            className="text-info"
                            style={{ cursor: 'pointer', textDecoration: 'underline' }}
                            onClick={() => handleOrderClick(order.orderId)}
                          >
                            {order.orderId}
                          </span>
                        </td>
                        <td>{formatDate(order.orderDate)}</td>
                        <td>{order.customerName}</td>
                        <td className="text-center">
                          <Badge bg="secondary">{order.lineItemsCount}</Badge>
                        </td>
                        <td className="text-center">
                          <Badge bg="success">{order.readyItems.length}</Badge>
                        </td>
                        <td>
                          <div className="d-flex flex-column gap-1" style={{ maxWidth: '300px' }}>
                            {order.readyItems.map((item, itemIndex) => (
                              <div key={itemIndex} className="small">
                                <span className="badge bg-light text-dark me-1">{item.sku}</span>
                                <span className="text-truncate" style={{ maxWidth: '150px', display: 'inline-block' }}>
                                  {item.name}
                                </span>
                                <span className="badge bg-info ms-1">×{item.quantity}</span>
                              </div>
                            ))}
                          </div>
                        </td>
                        <td className="text-center">
                          <div className="d-flex align-items-center justify-content-center gap-2">
                            <OrderLabelPrintButton order={order.originalOrder} variant="outline-secondary" showText={false} />
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => handleOrderClick(order.orderId)}
                              style={{ fontSize: '12px' }}
                            >
                              View Order
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              <div className="mt-3 text-muted">
                <small>Found {readyOrders.length} order(s) with items ready for outbound</small>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="text-muted">
                <p>No orders ready for outbound</p>
                <small>Orders with items that have INBOUNDED status will appear here</small>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Help Section */}
      <Card>
        <CardContent className="py-3">
          <div className="text-muted">
            <small>
              <strong>How to use:</strong>
              <ul className="mb-0 mt-1">
                <li>Click "Refresh" to load the latest orders ready for outbound</li>
                <li>Orders shown here have at least one line item with "INBOUNDED" status</li>
                <li>Click "View Order" to see the full order details and process outbound</li>
                <li>Only items marked as INBOUNDED are ready for outbound processing</li>
                <li>This page uses the dedicated readyToBeOutbonded API for optimized results</li>
              </ul>
            </small>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
